/**
 * GradientMapCompositor
 *
 * Composites a set of grayscale source images onto a canvas, recolouring each
 * layer with a chosen hex color using the gradient-map algorithm.
 *
 * Algorithm (ported from the color-change-admin WP plugin):
 *   For each pixel, compute brightness = avg(R, G, B).
 *   Sample target color from a monochromatic gradient canvas.
 *   If brightness > 127.5 → blend toward highlight:
 *       pixel = targetColor * ((255 - brightness) / 127.5) + (brightness - (255 - brightness))
 *   Else → blend toward shadow:
 *       pixel = targetColor * (brightness / 127.5)
 *
 * This preserves the shading/highlights of the grayscale image while mapping
 * midtones to the chosen color.
 */

export interface GradientLayer {
    /** URL of the grayscale source PNG */
    src: string;
    /** Hex color to map this layer to, e.g. "#CC2200" */
    hexColor: string;
    /** Stacking order; lower = drawn first (behind) */
    zIndex: number;
    /** Optional opacity 0–1, default 1 */
    opacity?: number;
}

export class GradientMapCompositor {
    // Shared off-screen canvases (reused across calls)
    private readonly workCanvas: HTMLCanvasElement;
    private readonly workCtx: CanvasRenderingContext2D;
    private readonly gradCanvas: HTMLCanvasElement;
    private readonly gradCtx: CanvasRenderingContext2D;
    private readonly imageCache = new Map<string, HTMLImageElement>();

    constructor() {
        this.workCanvas = document.createElement('canvas');
        const wCtx = this.workCanvas.getContext('2d', { willReadFrequently: true });
        if (!wCtx) throw new Error('2D canvas context unavailable');
        this.workCtx = wCtx;

        this.gradCanvas = document.createElement('canvas');
        const gCtx = this.gradCanvas.getContext('2d', { willReadFrequently: true });
        if (!gCtx) throw new Error('2D canvas context unavailable');
        this.gradCtx = gCtx;
    }

    /**
     * Composite all layers onto the target canvas.
     * Layers with an empty hexColor are drawn as-is (useful for base shadows/outlines).
     */
    async composite(
        target: HTMLCanvasElement,
        layers: GradientLayer[]
    ): Promise<void> {
        const sorted = [...layers].sort((a, b) => a.zIndex - b.zIndex);

        const ctx = target.getContext('2d');
        if (!ctx) return;

        for (const layer of sorted) {
            const img = await this.loadImage(layer.src);

            // Resize the target canvas to match the first image's natural dimensions.
            // This must happen before clearRect so the canvas ratio is correct.
            if (target.width !== img.naturalWidth || target.height !== img.naturalHeight) {
                target.width = img.naturalWidth;
                target.height = img.naturalHeight;
            }

            // Ensure work canvas matches image size
            if (this.workCanvas.width !== img.naturalWidth || this.workCanvas.height !== img.naturalHeight) {
                this.workCanvas.width = img.naturalWidth;
                this.workCanvas.height = img.naturalHeight;
            }

            let drawn: HTMLCanvasElement | HTMLImageElement;

            if (!layer.hexColor) {
                // No color mapping — draw as-is
                drawn = img;
            } else {
                drawn = await this.applyGradientMap(img, layer.hexColor);
            }

            ctx.globalAlpha = layer.opacity ?? 1;
            ctx.drawImage(drawn, 0, 0, target.width, target.height);
        }

        ctx.globalAlpha = 1;
    }

    /**
     * Apply gradient-map color transformation to a loaded image element.
     * Returns a canvas element containing the recoloured result.
     */
    async applyGradientMap(
        img: HTMLImageElement,
        hexColor: string
    ): Promise<HTMLCanvasElement> {
        const w = img.naturalWidth;
        const h = img.naturalHeight;

        this.workCanvas.width = w;
        this.workCanvas.height = h;
        this.workCtx.drawImage(img, 0, 0);

        const imageData = this.workCtx.getImageData(0, 0, w, h);
        const pixels = imageData.data;
        const gradData = this.buildGradient(hexColor);
        const gradPixels = gradData.data;
        const gradSize = gradData.width; // square gradient

        for (let i = 0; i < w * h; i++) {
            const a = pixels[i * 4 + 3];
            if (a === 0) continue; // skip fully transparent

            const brightness = (pixels[i * 4] + pixels[i * 4 + 1] + pixels[i * 4 + 2]) / 3;

            const y = Math.floor(i / w);
            const x = i - y * w;
            const gx = Math.round((x / w) * (gradSize - 1));
            const gy = Math.round((y / h) * (gradSize - 1));
            const gOff = (gy * gradSize + gx) * 4;

            const cr = gradPixels[gOff];
            const cg = gradPixels[gOff + 1];
            const cb = gradPixels[gOff + 2];

            if (brightness > 127.5) {
                const value = (255 - brightness) / 127.5;
                const min = brightness - (255 - brightness);
                pixels[i * 4] = cr * value + min;
                pixels[i * 4 + 1] = cg * value + min;
                pixels[i * 4 + 2] = cb * value + min;
            } else {
                const ratio = brightness / 127.5;
                pixels[i * 4] = cr * ratio;
                pixels[i * 4 + 1] = cg * ratio;
                pixels[i * 4 + 2] = cb * ratio;
            }
        }

        this.workCtx.putImageData(imageData, 0, 0);

        // Return a frozen copy so the original workCanvas can be reused
        const result = document.createElement('canvas');
        result.width = w;
        result.height = h;
        result.getContext('2d')!.drawImage(this.workCanvas, 0, 0);
        return result;
    }

    /** Export current target canvas state as a data URL. */
    toDataURL(canvas: HTMLCanvasElement, type = 'image/png', quality = 1): string {
        return canvas.toDataURL(type, quality);
    }

    // ── private helpers ────────────────────────────────────────────────────

    /**
     * Build a 256×256 monochromatic gradient canvas from black → hex → white
     * at 0° (vertical, top-to-bottom).  Returns its ImageData.
     */
    private buildGradient(hexColor: string): ImageData {
        const size = 256;
        this.gradCanvas.width = size;
        this.gradCanvas.height = size;

        // Create a top→bottom gradient: #000000 → hexColor → #ffffff
        const grad = this.gradCtx.createLinearGradient(0, 0, 0, size);
        grad.addColorStop(0, '#000000');
        grad.addColorStop(0.5, hexColor);
        grad.addColorStop(1, '#ffffff');

        this.gradCtx.fillStyle = grad;
        this.gradCtx.fillRect(0, 0, size, size);

        return this.gradCtx.getImageData(0, 0, size, size);
    }

    private loadImage(src: string): Promise<HTMLImageElement> {
        const cached = this.imageCache.get(src);
        if (cached) return Promise.resolve(cached);

        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => { this.imageCache.set(src, img); resolve(img); };
            img.onerror = () => reject(new Error(`Failed to load layer: ${src}`));
            img.src = src;
        });
    }
}
